import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["list", "badge", "desktopToggle"]

  async connect() {
    await this.registerServiceWorker()
    this.subscribe()
    this.syncToggleFromPreference()
    this.attachDropdownOpenHandler()
    this.listenForServiceWorkerMessages()
    this.clearBadgeOnFocus()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  async registerServiceWorker() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('[Notifications] Service Worker or Push API not supported')
      return
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js')
      this.swRegistration = registration
      console.log('[Notifications] Service Worker registered')
      
      // Check for existing subscription
      const subscription = await registration.pushManager.getSubscription()
      if (subscription && this.hasDesktopToggleTarget) {
        this.desktopToggleTarget.checked = true
      }
    } catch (error) {
      console.warn('[Notifications] Service Worker registration failed:', error)
    }
  }

  listenForServiceWorkerMessages() {
    if (!('serviceWorker' in navigator)) return
    
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
        window.location.href = event.data.url
      }
    })
  }

  async syncToggleFromPreference() {
    if (!this.hasDesktopToggleTarget) return
    try {
      // Reflect browser permission only; no persistence
      this.desktopToggleTarget.checked = ("Notification" in window) && Notification.permission === "granted"
    } catch(e) {}
  }

  subscribe() {
    // Create an ActionCable consumer explicitly
    let consumer
    try {
      // Reuse a singleton consumer to avoid multiple websocket connections
      consumer = window.__notificationsConsumer || createConsumer()
      window.__notificationsConsumer = consumer
    } catch(e) {
      console.warn("[Notifications] Failed to create ActionCable consumer", e)
      return
    }

    this.subscription = consumer.subscriptions.create({ channel: "NotificationsChannel" }, {
      connected: () => {
        try { console.log('[Notifications] Cable connected') } catch(_) {}
      },
      disconnected: () => {
        try { console.log('[Notifications] Cable disconnected') } catch(_) {}
      },
      rejected: () => {
        try { console.warn('[Notifications] Subscription rejected') } catch(_) {}
      },
      received: (data) => this.received(data)
    })
  }

  received(data) {
    // Desktop broadcasts piggyback on in_app events. Avoid double increment.
    if (!data.desktop) {
      this.appendToList(data)
      this.incrementBadge()
    }
    if (data.desktop && this.hasDesktopToggleTarget && this.desktopToggleTarget.checked) this.showDesktop(data)
  }

  appendToList(data) {
    if (!this.hasListTarget) return
    const item = document.createElement('div')
    item.className = 'p-3 border-b border-base-300 hover:bg-base-200'
    item.innerHTML = `
      <div class="flex items-start gap-3">
        <div class="mt-0.5">
          <iconify-icon icon="${data.icon || 'ph:bell'}"></iconify-icon>
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium">${data.title || 'Notification'}</div>
          <div class="text-xs opacity-70">${data.body || ''}</div>
        </div>
        ${data.url ? `<a href="${data.url}" class="btn btn-ghost btn-xs">View</a>` : ''}
      </div>
    `
    this.listTarget.prepend(item)
  }

  incrementBadge() {
    if (!this.hasBadgeTarget) return
    const current = parseInt(this.badgeTarget.dataset.count || '0', 10)
    const next = current + 1
    this.badgeTarget.dataset.count = String(next)
    this.badgeTarget.textContent = String(next)
    this.badgeTarget.classList.remove('hidden')
  }

  showDesktop(data) {
    if (!("Notification" in window)) return
    if (Notification.permission !== "granted") return
    const n = new Notification(data.title || 'Notification', {
      body: data.body,
      icon: data.icon ? undefined : undefined
    })
    if (data.url) {
      n.onclick = () => window.open(data.url, '_blank')
    }
  }

  async toggleDesktop(event) {
    const enabled = !!event.target.checked
    
    if (!enabled) {
      // User is disabling notifications - unsubscribe from push
      await this.unsubscribeFromPush()
      return
    }
    
    // User is enabling notifications
    if (!("Notification" in window)) {
      console.warn('[Notifications] Notification API not supported')
      event.target.checked = false
      return
    }

    // Request permission if needed
    if (Notification.permission === "default") {
      const result = await Notification.requestPermission()
      if (result !== "granted") {
        event.target.checked = false
        return
      }
    }
    
    if (Notification.permission !== "granted") {
      event.target.checked = false
      return
    }

    // Subscribe to push notifications
    await this.subscribeToPush()
  }

  async subscribeToPush() {
    if (!this.swRegistration) {
      console.warn('[Notifications] Service Worker not registered')
      return
    }

    try {
      // Check if already subscribed
      let subscription = await this.swRegistration.pushManager.getSubscription()
      
      if (!subscription) {
        // Get VAPID public key from server
        const vapidKey = await this.getVAPIDPublicKey()
        
        subscription = await this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(vapidKey)
        })
      }
      
      // Send subscription to server
      await this.savePushSubscription(subscription)
      console.log('[Notifications] Push subscription successful')
    } catch (error) {
      console.error('[Notifications] Push subscription failed:', error)
      if (this.hasDesktopToggleTarget) {
        this.desktopToggleTarget.checked = false
      }
    }
  }

  async unsubscribeFromPush() {
    if (!this.swRegistration) return

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription()
      if (subscription) {
        await subscription.unsubscribe()
        await this.removePushSubscription(subscription)
        console.log('[Notifications] Push unsubscribed')
      }
    } catch (error) {
      console.error('[Notifications] Push unsubscribe failed:', error)
    }
  }

  async getVAPIDPublicKey() {
    const response = await fetch('/notifications/vapid_public_key')
    const data = await response.json()
    return data.publicKey
  }

  async savePushSubscription(subscription) {
    const response = await fetch('/notifications/subscribe_push', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({ subscription: subscription.toJSON() })
    })
    
    if (!response.ok) {
      throw new Error('Failed to save push subscription')
    }
  }

  async removePushSubscription(subscription) {
    await fetch('/notifications/unsubscribe_push', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({ subscription: subscription.toJSON() })
    })
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/')
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  attachDropdownOpenHandler() {
    // Dropdown container is the parent element of the button
    const dropdown = this.element
    if (!dropdown) return
    const button = dropdown.querySelector('button')
    if (!button) return
    button.addEventListener('click', () => {
      // On open, mark notifications as viewed and refresh list
      setTimeout(() => {
        this.markNotificationsViewed()
        this.reloadList()
      }, 0)
    })
  }

  async markNotificationsViewed() {
    try {
      const formData = new FormData()
      formData.append('preference_key', 'last_notifications_viewed_at')
      const response = await fetch('/users/update_preferences', {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': this.getCSRFToken() },
        body: formData
      })
      if (response.ok) {
        // Reset badge to zero
        if (this.hasBadgeTarget) {
          this.badgeTarget.dataset.count = '0'
          this.badgeTarget.textContent = '0'
          this.badgeTarget.classList.add('hidden')
        }
      }
    } catch (e) {}
  }

  reloadList() {
    fetch('/notifications')
      .then(r => r.text())
      .then(html => {
        if (!this.hasListTarget) return
        this.listTarget.innerHTML = html
      })
      .catch(() => {})
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }

  clearBadgeOnFocus() {
    // Clear badge when user opens/focuses the app
    window.addEventListener('focus', async () => {
      try {
        if ('clearAppBadge' in navigator) {
          await navigator.clearAppBadge()
        }
      } catch (e) {
        console.log('[Notifications] Badge clear failed:', e)
      }
    })
  }
}


